import { DeviceTypes, QueryCondition, Query, StudyCoughEventType, User, CoughSessionStatusEnum, OverreadStatusEnum, OverreadStatusIdEnum } from "../misc/api/apiTypes";
import * as GenericMessages from "../misc/api/DeviceLinkApi/GenericMessages";
import * as VitaloJak1Api from '../misc/api/DeviceLinkApi/VitaloJak1';
import * as VitaloJak2Api from '../misc/api/DeviceLinkApi/VitaloJak2';
import * as VitaloJak1Messages from '../misc/api/DeviceLinkApi/VitaloJak1Messages';
import * as VitaloJak2Messages from '../misc/api/DeviceLinkApi/VitaloJak2Messages';
import { Device } from '../misc/api/DeviceLinkApi/Devices';
import { authenticatedFetchRetry } from '../misc/api/FetchRetry';
import { RecordingFile } from '../misc/api/DeviceLinkApi/Models/Cough/RecordingFileEnum';
import * as Moment from 'moment';
import { VitaloJak2Recording, VitaloJak2LogFileRecord } from '../misc/api/DeviceLinkApi/VitaloJak2';
import { DevicesAction, DevicesState, SelectActiveSessionForUploadAction, SetDeviceOverallUploadStatusAction, SetInteractionModalStepAction, SetUploadStatusAction, UploadStatus } from './DevicesNewReducer';
import { CoughAnalysisSet, CoughAnalysisSetEventCreation, WebPortalClient, CoughSession } from "webportalclient";
import { CoughAnalysisSetTypeEnum } from "../misc/api/CoughAnalysisSetTypes";
import { gzip } from "pako";
import { useWebPortalApiClient, WebPortalLegacyClientInternal } from "../misc/api/WebPortalTokenClient";
import { CoughMarshalClient } from "@vitalograph/coughmarshalclient";
import { User as AuthUser } from "oidc-client-ts";
import { SendAlertToServer } from "./AlertUtility";
import { DeviceNotification } from "../misc/api/DeviceLinkApi/Models/DeviceNotification";

declare const rootUrl: string;
declare const coughMarshalRootUrl: string;

const hexToBase64 = (str) => {
    return btoa(String.fromCharCode.apply(null,
        str.replace(/\r|\n/g, "").replace(/([\da-fA-F]{2}) ?/g, "0x$1 ").replace(/ +$/, "").split(" "))
    );
}

const retryCount = 3;
const retryInterval = 50000;
const chunkSize = 10485760;

export class UploadCoughSessionsAsyncAction {

    public static UploadCoughSessions = async (client: WebPortalClient, legacyClient: WebPortalLegacyClientInternal, coughMarshalClient: CoughMarshalClient, user: AuthUser, dispatch: React.Dispatch<DevicesAction>, state: DevicesState, currentUser: User, studyId: string, studyProtocol: string) => {
        try {
            const configuredDevices = state.selectedDevices.filter(x => x.serial !== 'Not Configured');

            const results = [];

            for (const device of configuredDevices) {
                try {
                    let result = null;
                    dispatch(SetDeviceOverallUploadStatusAction.create(device.id, UploadStatus.Uploading));
                    switch (device.type) {
                        case DeviceTypes.VitaloJAK1:
                            result = await UploadCoughSessionsAsyncAction.UploadSessionVitaloJak1(coughMarshalClient, client, legacyClient, user, dispatch, device, studyId, studyProtocol, currentUser);
                            break;
                        case DeviceTypes.VitaloJAK2:
                            result = await UploadCoughSessionsAsyncAction.UploadMultiSessionsVitaloJak2(coughMarshalClient, client, legacyClient, user, state, dispatch, device, currentUser, studyId, studyProtocol);
                            break;
                    }

                    results.push({ id: device.id, result: result });
                    if (!result) {
                        dispatch(SetDeviceOverallUploadStatusAction.create(device.id, UploadStatus.Failed));
                        continue;
                    } else {
                        dispatch(SetDeviceOverallUploadStatusAction.create(device.id, UploadStatus.Uploaded));
                        continue;
                    }
                } catch (err) {
                    dispatch(SetDeviceOverallUploadStatusAction.create(device.id, UploadStatus.PartialFailed));
                    results.push({ id: device.id, result: false, message: err.message });

                    if (device.type === DeviceTypes.VitaloJAK2) {
                        await SendAlertToServer(client, {
                            id: 9,
                            headline: "Not all sessions were uploaded correctly. Please refer to your site manual for further instructions.",
                            additionalContent: `Device triggering the alert: Serial Number: '${device.serial}'`,
                            originatingIdentifier: `${device.serial}_Uploaded_${Moment().format("YYYY-MM-DD HH:mm:ss")}`,
                        } as DeviceNotification);
                    }
                    continue;
                }
            }

            if (results.filter(x => !x.result).length === 0) {
                dispatch(SetUploadStatusAction.create(GenericMessages.UploadCompleteMessage));
                return dispatch(SetInteractionModalStepAction.create(4));
            } else  {
                const specificErrors = results.filter(x => !x.result).map(y => y.message).join(' ');
                if (specificErrors.valueOf() === VitaloJak2Messages.FailedToUploadData && state.activeSelectedDevice.type === DeviceTypes.VitaloJAK2) {
                    dispatch(SetUploadStatusAction.create(specificErrors));
                    return dispatch(SetInteractionModalStepAction.create(11));
                }
                else {
                    if (specificErrors !== undefined && specificErrors !== "") {
                        dispatch(SetUploadStatusAction.create(specificErrors));
                    }
                    return dispatch(SetInteractionModalStepAction.create(5));
                }
            }
        }
        catch (err) {
            if (err.message !== null)
                dispatch(SetUploadStatusAction.create(err.message));
            else
                dispatch(SetUploadStatusAction.create(GenericMessages.FailedToUploadMessage));
            return dispatch(SetInteractionModalStepAction.create(5));
        }
    }
    
    private static UploadMultiSessionsVitaloJak2 = async (coughMarshalClient: CoughMarshalClient, client: WebPortalClient, legacyClient: WebPortalLegacyClientInternal, user: AuthUser, state: DevicesState, dispatch: React.Dispatch<DevicesAction>, device: Device, currentUser: User, studyId: string, studyProtocol: string) => {
        const results = [];

        const recordingsOnDevice = await VitaloJak2Api.ApiGetRecordings(device.id);
        const recordingsIdOnDevice = recordingsOnDevice.map(x => x.externalCode);
        let recordingsOnCoughMarshal;
        try {
            recordingsOnCoughMarshal = await coughMarshalClient.getRecordings(undefined, `id in (${recordingsIdOnDevice}) and status ge 2`);
        } catch (err) {
            throw new Error(`Cannot query cough service for recording status '${err.message}'`);
        }

        if (!recordingsOnCoughMarshal) {
            // TODO: Cough Marshal might be offline!
            return false;
        }

        if (recordingsOnCoughMarshal.value.length !== recordingsOnDevice.length) {
            for (const sessionId of state.upload.selectedSessionIds) {

                dispatch(SelectActiveSessionForUploadAction.create(sessionId));
                try {
                    const uploadResult = await UploadCoughSessionsAsyncAction.UploadSessionVitaloJak2(coughMarshalClient, client, legacyClient, user, state, dispatch, device, sessionId, currentUser, studyId, studyProtocol);
                    if (uploadResult) {
                        await client.patchCoughSession({
                            configured: true,
                        }, sessionId);
                    }
                }
                catch (err) {
                    throw new Error(err.message);
                }
            }
        }

        const coughSessionsWithNoEndTime = await client.getCoughSessions(undefined, `not(id in (${state.upload.selectedSessionIds.join(",")})) and uploadEndTime eq null and serialNumber eq '${state.activeSelectedDevice.serial.replace("MD", "")}'`)

        for (const session of coughSessionsWithNoEndTime.value) {
            await client.patchCoughSession({
                suppressed: true,
                configured: false,
            }, session.id);
        }

        const coughSessionsAfterUpload = await coughMarshalClient.getRecordings(undefined, `id in (${recordingsIdOnDevice}) and status ge 2`);

        if (coughSessionsAfterUpload.value.length === recordingsOnDevice.length) {
            dispatch(SetUploadStatusAction.create(VitaloJak2Messages.FormattingDevice));
            const resetResult = await VitaloJak2Api.ApiReset(device.id);

            results.push({ id: device.id, result: resetResult });

            if (!resetResult)
                throw new Error(VitaloJak2Messages.FailedToFormatMessageWithUpload);

        } else {
            throw new Error(VitaloJak2Messages.NotAllRecordingsUploadedMessage);
        }

        return results;
    }

    private static UploadSessionVitaloJak1 = async (coughMarshalClient: CoughMarshalClient, client: WebPortalClient, legacyClient: WebPortalLegacyClientInternal, user: AuthUser, dispatch: React.Dispatch<DevicesAction>, device: Device, studyId: string, studyProtocol: string, currentUser: User) => {
        const cardConfiguration = await VitaloJak1Api.ApiGetConfiguration(device.id);

        if (!cardConfiguration) {
            dispatch(SetUploadStatusAction.create(VitaloJak1Messages.CardNotConfigured));
            return false;
        }

        dispatch(SelectActiveSessionForUploadAction.create(cardConfiguration.assessmentGuid));
        const result = await UploadCoughSessionsAsyncAction.fetchParentSessionCough(client, cardConfiguration.assessmentGuid);

        if (result === null) {
            dispatch(SetUploadStatusAction.create(VitaloJak1Messages.FailedToFindParentSession));
            return false;
        }

        if (![CoughSessionStatusEnum.AwaitingData, CoughSessionStatusEnum.ReceivingData, CoughSessionStatusEnum.ReceivingDataMailed, CoughSessionStatusEnum.DeviceUnassigned].includes(result.status as CoughSessionStatusEnum)) {
            dispatch(SetUploadStatusAction.create(VitaloJak1Messages.AlreadyUploadedMessage));
            return false;
        }

        const sessionId = cardConfiguration.assessmentGuid;

        dispatch(SetUploadStatusAction.create(GenericMessages.CheckingForRecordingDataMessage));
        const { microphoneTotalLength, sensorTotalLength } = await UploadCoughSessionsAsyncAction.rangePreFlightWavCheck(device.id, VitaloJak1Api.ApiGetMicrophoneRecording, VitaloJak1Api.ApiGetSensorRecording);
        if (microphoneTotalLength !== sensorTotalLength) {
            dispatch(SetUploadStatusAction.create(GenericMessages.WavFilesIncomplete));
            return false;
        }

        let recordingTags = [studyProtocol];
        const tagsSetting = await legacyClient.getSettingsHelper('Vitalograph.WebPortal.Data.Settings.Cough.CoughMarshalRecordingTagsSetting', studyId);

        if (tagsSetting.Data.length !== 0) {
            const studyTags = tagsSetting.Data[0].Value.split(',').filter(s => s !== '');

            recordingTags = [
                ...recordingTags,
                ...studyTags
            ]
        }

        // Check if this is a new recording
        const thisCoughRecording = await coughMarshalClient.getRecordings(undefined, `id eq ${sessionId}`);

        const microphoneFullHash = await VitaloJak1Api.ApiGetMicrophoneRecordingHash(device.id);
        const sensorFullHash = await VitaloJak1Api.ApiGetSensorRecordingHash(device.id);

        if (thisCoughRecording.value.length === 0) {
            try {
                await coughMarshalClient.putRecording({
                    id: sessionId,
                    microphonehash: microphoneFullHash.hash,
                    sensorhash: sensorFullHash.hash,
                    tags: recordingTags,
                });
            } catch {
                dispatch(SetUploadStatusAction.create(VitaloJak2Messages.FailedToUploadData));
                throw new Error(VitaloJak2Messages.FailedToUploadData);
            }

            const metaResult = await client.patchCoughSession({
                status: CoughSessionStatusEnum.ReceivingData,
                recordingStart: cardConfiguration.started,
                technician: currentUser.Username
            }, sessionId);

            //It is possible for the recording start value to be corrupted, but that should not prevent an upload.
            if (!!cardConfiguration.started && !Moment(metaResult.recordingStart).isSame(cardConfiguration.started)) {
                dispatch(SetUploadStatusAction.create(VitaloJak2Messages.FailedToUploadData));
                throw new Error(VitaloJak2Messages.FailedToUploadData);
            }
        }

        const logFile = await VitaloJak1Api.ApiGetLogFile(device.id);

        const isMicrophoneUploaded = await UploadCoughSessionsAsyncAction.uploadChunks(coughMarshalClient, user, sessionId, dispatch, device.id, "24hrWAV1.wav", RecordingFile.Microphone, microphoneTotalLength, chunkSize, VitaloJak1Api.ApiGetMicrophoneRecording, microphoneFullHash.hash, null, retryCount, retryInterval);

        if (!isMicrophoneUploaded) {
            dispatch(SetUploadStatusAction.create(VitaloJak2Messages.FailedToUploadData));
            throw new Error(VitaloJak2Messages.FailedToUploadData);
        }

        const isSensorUploaded = await UploadCoughSessionsAsyncAction.uploadChunks(coughMarshalClient, user, sessionId, dispatch, device.id, "24hrWAV2.wav", RecordingFile.Sensor, sensorTotalLength, chunkSize, VitaloJak1Api.ApiGetSensorRecording, sensorFullHash.hash, null, retryCount, retryInterval);

        if (!isSensorUploaded) {
            dispatch(SetUploadStatusAction.create(VitaloJak2Messages.FailedToUploadData));
            throw new Error(VitaloJak2Messages.FailedToUploadData);
        }

        if (logFile !== null) {
            const isLogUploaded = await UploadCoughSessionsAsyncAction.uploadVJILog(coughMarshalClient, device.id, sessionId, user);
        }

        dispatch(SetUploadStatusAction.create(GenericMessages.CheckingRecordingIntegrityMessage));

        try {
            await coughMarshalClient.upload_Verify(sessionId);
        } catch {
            dispatch(SetUploadStatusAction.create(GenericMessages.RecordingIntegrityFailedMessage));
            return false;
        }

        return await legacyClient.uploadFinalize(sessionId);
    }


    private static UploadSessionVitaloJak2 = async (coughMarshalClient: CoughMarshalClient, client: WebPortalClient, legacyClient: WebPortalLegacyClientInternal, user: AuthUser, currentState: DevicesState, dispatch: React.Dispatch<DevicesAction>, device: Device, sessionId: string, currentUser: User, studyId: string, studyProtocol: string) => {
        const result = await UploadCoughSessionsAsyncAction.fetchParentSessionCough(client, sessionId);

        if (result === null) {
            dispatch(SetUploadStatusAction.create(VitaloJak2Messages.FailedToFindParentSession));
            return false;
        }

        if (![CoughSessionStatusEnum.AwaitingData, CoughSessionStatusEnum.ReceivingData, CoughSessionStatusEnum.ReceivingDataMailed, CoughSessionStatusEnum.DeviceUnassigned].includes(result.status as CoughSessionStatusEnum)) {
            dispatch(SetUploadStatusAction.create(VitaloJak2Messages.AlreadyUploadedMessage));
            return false;
        }

        dispatch(SetUploadStatusAction.create(GenericMessages.CheckingForRecordingDataMessage));

        const deviceInformation = await VitaloJak2Api.ApiDeviceInformation(device.id);
        const recordings = await VitaloJak2Api.ApiGetRecordings(device.id);
        const recordingToUpload = recordings.find(x => x.externalCode === sessionId);

        const { microphoneTotalLength, sensorTotalLength } = await UploadCoughSessionsAsyncAction.rangePreFlightWavCheck(device.id, VitaloJak2Api.ApiGetMicrophoneRecording, VitaloJak2Api.ApiGetSensorRecording, recordingToUpload.id);
        if (microphoneTotalLength !== sensorTotalLength) {
            dispatch(SetUploadStatusAction.create(GenericMessages.WavFilesIncomplete));
            return false;
        }

        let recordingTags = [studyProtocol];
        const tagsSetting = await legacyClient.getSettingsHelper('Vitalograph.WebPortal.Data.Settings.Cough.CoughMarshalRecordingTagsSetting', studyId);

        if (tagsSetting.Data.length !== 0) {
            const studyTags = tagsSetting.Data[0].Value.split(',').filter(s => s !== '');

            recordingTags = [
                ...recordingTags,
                ...studyTags
            ]
        }

        // Check if this is a new recording
        const thisCoughRecording = await coughMarshalClient.getRecordings(undefined, `id eq ${sessionId}`);

        const microphoneFullHash = await VitaloJak2Api.ApiGetMicrophoneRecordingHash(device.id, recordingToUpload.id);
        const sensorFullHash = await VitaloJak2Api.ApiGetSensorRecordingHash(device.id, recordingToUpload.id);

        if (thisCoughRecording.value.length === 0) {
            try {
                await coughMarshalClient.putRecording({
                    id: sessionId,
                    microphonehash: microphoneFullHash.hash,
                    sensorhash: sensorFullHash.hash,
                    tags: recordingTags,
                });
            } catch {
                dispatch(SetUploadStatusAction.create(VitaloJak2Messages.FailedToUploadData));
                throw new Error(VitaloJak2Messages.FailedToUploadData);
            }
        }

        const { started } = await UploadCoughSessionsAsyncAction.calculateDuration(device.id, recordingToUpload);

        const metaResult = await client.patchCoughSession({
            status: CoughSessionStatusEnum.ReceivingData,
            recordingStart: started,
            serialNumber: deviceInformation.deviceSerial.trim(),
            softwareVersion: deviceInformation.softwareRevision,
            hardwareVersion: deviceInformation.hardwareRevision,
            softwareReference: deviceInformation.softwareId,
            technician: currentUser.Username,
            uploadStartTime: new Date().toISOString()
        }, sessionId);

        if (!Moment(metaResult.recordingStart).isSame(started)) {
            dispatch(SetUploadStatusAction.create(GenericMessages.FailedToUploadMessage));
            return false;
        }

        const logFile = await VitaloJak2Api.ApiGetLogFile(device.id, recordingToUpload.id);
        await UploadCoughSessionsAsyncAction.uploadDeviceEvents(client, legacyClient, studyId, sessionId, logFile.records);
        
        const isMicrophoneUploaded = await UploadCoughSessionsAsyncAction.uploadChunks(coughMarshalClient, user, sessionId, dispatch, device.id, "24hrWAV1.wav", RecordingFile.Microphone, microphoneTotalLength, chunkSize, VitaloJak2Api.ApiGetMicrophoneRecording, microphoneFullHash.hash, recordingToUpload.id, retryCount, retryInterval);

        if (!isMicrophoneUploaded) {
            dispatch(SetUploadStatusAction.create(VitaloJak2Messages.FailedToUploadData));
            throw new Error(VitaloJak2Messages.FailedToUploadData);
        }

        const isSensorUploaded = await UploadCoughSessionsAsyncAction.uploadChunks(coughMarshalClient, user, sessionId, dispatch, device.id, "24hrWAV2.wav", RecordingFile.Sensor, sensorTotalLength, chunkSize, VitaloJak2Api.ApiGetSensorRecording, sensorFullHash.hash, recordingToUpload.id, retryCount, retryInterval);

        if (!isSensorUploaded) {
            dispatch(SetUploadStatusAction.create(VitaloJak2Messages.FailedToUploadData));
            throw new Error(VitaloJak2Messages.FailedToUploadData);
        }

        const isLogUploaded = await UploadCoughSessionsAsyncAction.uploadLog(coughMarshalClient, user, device.id, sessionId, recordingToUpload.id);
        
        if (!isLogUploaded) {
            dispatch(SetUploadStatusAction.create(VitaloJak2Messages.FailedToUploadData));
            throw new Error(VitaloJak2Messages.FailedToUploadData);
        }

        const isAuditTrailUploaded = await UploadCoughSessionsAsyncAction.uploadAuditTrail(coughMarshalClient, user, device.id, sessionId, recordingToUpload.id);

        if (!isAuditTrailUploaded) {
            dispatch(SetUploadStatusAction.create(VitaloJak2Messages.FailedToUploadData));
            throw new Error(VitaloJak2Messages.FailedToUploadData);
        }

        dispatch(SetUploadStatusAction.create(GenericMessages.CheckingRecordingIntegrityMessage));

        try {
            await authenticatedFetchRetry(user, `${coughMarshalRootUrl}/api/v1/uploads/${sessionId}/complete`, {
                method: "POST"
            }, retryCount, retryInterval);
        } catch {
            dispatch(SetUploadStatusAction.create(GenericMessages.RecordingIntegrityFailedMessage));
            return false;
        }

        return await legacyClient.uploadFinalize(sessionId);
    }

    private static uploadDeviceEvents = async (webPortalClient: WebPortalClient, legacyClient: WebPortalLegacyClientInternal, studyId: string, sessionId: string, records: Array<VitaloJak2LogFileRecord>) => {
        const buttonEventCodes = [
            VitaloJak2Api.VitaloJak2LogEventCode.button1Pressed,
            VitaloJak2Api.VitaloJak2LogEventCode.button2Pressed,
            VitaloJak2Api.VitaloJak2LogEventCode.button3Pressed,
            VitaloJak2Api.VitaloJak2LogEventCode.button4Pressed,
            VitaloJak2Api.VitaloJak2LogEventCode.muteOn,
            VitaloJak2Api.VitaloJak2LogEventCode.muteOff,
        ];

        const events = records.filter(x => buttonEventCodes.includes(x.eventCode));

        if (events.length > 0) {
            let subjectAnalysisSet: CoughAnalysisSet;
            const analysisSetsResult = await webPortalClient.getUnrestrictedCoughAnalysisSets(undefined, `sessionId eq ${sessionId} and type eq '${CoughAnalysisSetTypeEnum.DeviceEvents}'`);

            if (analysisSetsResult.value.length === 0) {
                subjectAnalysisSet = await webPortalClient.postCoughAnalysisSet({
                    sessionId: sessionId,
                    type: CoughAnalysisSetTypeEnum.DeviceEvents,
                    status: OverreadStatusIdEnum.Accepted,
                    primary: true
                });
            } else {
                subjectAnalysisSet = analysisSetsResult.value[0];
            }

            const eventTypes = await legacyClient.getStudyCoughEventTypes({
                Filters: [
                    {
                        Member: "StudyId",
                        Values: [studyId]
                    }
                ]
            });

            let eventsFiltered = events
                .filter(x => buttonEventCodes.includes(x.eventCode))
                .sort((a, b) => Moment(a.dateTime).diff(Moment(b.dateTime)));

            for (const [index, value] of eventsFiltered.entries()) {
                let eventType: StudyCoughEventType = null;
                let rawEnd = null;
                switch (value.eventCode) {
                    case VitaloJak2Api.VitaloJak2LogEventCode.button1Pressed:
                        eventType = eventTypes.Data.find(x => x.AssociatedDeviceButton === 1);
                        break;
                    case VitaloJak2Api.VitaloJak2LogEventCode.button2Pressed:
                        eventType = eventTypes.Data.find(x => x.AssociatedDeviceButton === 2);
                        break;
                    case VitaloJak2Api.VitaloJak2LogEventCode.button3Pressed:
                        eventType = eventTypes.Data.find(x => x.AssociatedDeviceButton === 3);
                        break;
                    case VitaloJak2Api.VitaloJak2LogEventCode.button4Pressed:
                        eventType = eventTypes.Data.find(x => x.AssociatedDeviceButton === 4);
                        break;
                    case VitaloJak2Api.VitaloJak2LogEventCode.muteOn:
                        eventType = eventTypes.Data.find(x => x.AssociatedDeviceButton === 4);

                        let tailOfList = eventsFiltered.slice(index + 1, eventsFiltered.length);
                        rawEnd = tailOfList.find(x => x.eventCode === VitaloJak2Api.VitaloJak2LogEventCode.muteOff);
                        break;
                }

                if (eventType) {
                    const recordingStart = records.find(x => x.eventCode === VitaloJak2Api.VitaloJak2LogEventCode.recordingStarted).dateTime;

                    const analysisSetEvent: CoughAnalysisSetEventCreation = {
                        analysisSetId: subjectAnalysisSet.id,
                        eventTypeId: eventType.Id,
                        rawStart: Moment(value.dateTime).diff(Moment(recordingStart)) / 1000,
                        rawEnd: rawEnd ? Moment(rawEnd.dateTime).diff(Moment(recordingStart)) / 1000 : null,
                    }

                    if (eventType.IsSegmented && analysisSetEvent.rawEnd === null)
                        return; // Ignore mute events missing end time (existing device bug)

                    //Don't want 0 duration events.
                    if (analysisSetEvent.rawEnd === null || analysisSetEvent.rawEnd !== analysisSetEvent.rawStart) {

                        const eventResult = await webPortalClient.getCoughAnalysisSetEvents(undefined,
                            `analysisSetId eq ${analysisSetEvent.analysisSetId} and eventTypeId eq ${analysisSetEvent.eventTypeId} and rawStart eq ${analysisSetEvent.rawStart} and rawEnd eq ${analysisSetEvent.rawEnd}`
                        );
                        if (eventResult.value.length === 0) {
                            await webPortalClient.postCoughAnalysisSetEvent(analysisSetEvent);
                        }
                    }

                }
            }
        }
    }

    private static calculateDuration = async (deviceId: string, recording: VitaloJak2Recording): Promise<{ started: string }> => {
        const logFile = await VitaloJak2Api.ApiGetLogFile(deviceId, recording.id);
        const started = Moment(logFile.records.find(r => r.eventCode === VitaloJak2Api.VitaloJak2LogEventCode.recordingStarted).dateTime);

        return { started: started.format() };
    }

    private static fetchParentSessionCough = async (client: WebPortalClient, sessionId: string): Promise<CoughSession> => {
        return await client.getCoughSession(sessionId);
    }

    private static rangePreFlightWavCheck = async (deviceId: string,
        microphoneRecordingApi: (id: string, bytesFrom: number, bytesTo: number, recordingId?: string) => Promise<Response>,
        sensorRecordingApi: (id: string, bytesFrom: number, bytesTo: number, recordingId?: string) => Promise<Response>,
        recordingId?: string,
    ) => {
        const rangeRegEx = new RegExp(/bytes (\d*-\d*)\/(\d*)/);

        const microphoneRecordingResponse = await microphoneRecordingApi(deviceId, 0, 44, recordingId);
        const sensorRecordingResponse = await sensorRecordingApi(deviceId, 0, 44, recordingId);

        const microphoneTotalLength = parseInt(rangeRegEx.exec(microphoneRecordingResponse.headers.get('Content-Range'))[2]);
        const sensorTotalLength = parseInt(rangeRegEx.exec(sensorRecordingResponse.headers.get('Content-Range'))[2]);

        return { microphoneTotalLength, sensorTotalLength };
    }

    private static async uploadVJILog(coughMarshalClient: CoughMarshalClient, deviceId: string, sessionId: string, user: AuthUser): Promise<boolean> {
        const logFileFullHash = await VitaloJak1Api.ApiGetLogHash(deviceId);

        const logHeadResult = await authenticatedFetchRetry(user, `${coughMarshalRootUrl}/api/v1/uploads/${sessionId}`, {
            method: 'HEAD',
            headers: {
                'Content-Disposition': `attachment; filename="log.txt"`
            }
        }, retryCount, retryInterval)
            .then(response => response.headers.get('Content-Length'));

        if (!logHeadResult) {
            return false;
        }

        if (parseInt(logHeadResult) === logFileFullHash.totalFileLength) {
            return true;
        }

        const logFileStream = await VitaloJak1Api.ApiGetLogFileStream(deviceId);

        if (logFileStream.ok) {
            return await UploadCoughSessionsAsyncAction.uploadMiscToCoughMarshal(coughMarshalClient, user, logFileStream, sessionId, "log.txt", logFileFullHash.hash, retryCount, retryInterval);
        }
    }

    private static async uploadLog(coughMarshalClient: CoughMarshalClient, user: AuthUser, deviceId: string, sessionId: string, recordingId: string): Promise<boolean> {        
        const logFileFullHash = await VitaloJak2Api.ApiGetLogHash(deviceId, recordingId);

        const logHeadResult = await authenticatedFetchRetry(user, `${coughMarshalRootUrl}/api/v1/uploads/${sessionId}`, {
            method: 'HEAD',
            headers: {
                'Content-Disposition': `attachment; filename="log.txt"`
            }
        }, retryCount, retryInterval)
            .then(response => response.headers.get('Content-Length'));

        if (!logHeadResult) {
            return false;
        }

        if (parseInt(logHeadResult) === logFileFullHash.totalFileLength) {
            return true;
        }

        const logFileStream = await VitaloJak2Api.ApiGetLogFileStream(deviceId, recordingId);

        if (logFileStream.ok) {
            return await UploadCoughSessionsAsyncAction.uploadMiscToCoughMarshal(coughMarshalClient, user, logFileStream, sessionId, "log.txt", logFileFullHash.hash, retryCount, retryInterval);
        }
    }

    private static async uploadAuditTrail(coughMarshalClient: CoughMarshalClient, user: AuthUser, deviceId: string, sessionId: string, recordingId: string): Promise<boolean> {
        const auditTrailFullHash = await VitaloJak2Api.ApiGetAuditTrailHash(deviceId, recordingId);

        const auditHeadResult = await authenticatedFetchRetry(user, `${coughMarshalRootUrl}/api/v1/uploads/${sessionId}`, {
            method: 'HEAD',
            headers: {
                'Content-Disposition': `attachment; filename="audit_trail.txt"`
            }
        }, retryCount, retryInterval)
            .then(response => response.headers.get('Content-Length'));

        if (!auditHeadResult) {
            return false;
        }

        if (parseInt(auditHeadResult) === auditTrailFullHash.totalFileLength) {
            return true;
        }

        const auditTrailStream = await VitaloJak2Api.ApiGetAuditFileStream(deviceId);

        if (auditTrailStream.ok) {
            return await UploadCoughSessionsAsyncAction.uploadMiscToCoughMarshal(coughMarshalClient, user, auditTrailStream, sessionId, "audit_trail.txt", auditTrailFullHash.hash, retryCount, retryInterval);
        }
    }

    private static async uploadMiscToCoughMarshal(coughMarshalClient: CoughMarshalClient, user: AuthUser, streamResponse: Response, guid: string, filename: string, fullHash: string, retryCount: number, retryInterval: number) {
        const dataBytes = await streamResponse.arrayBuffer();
        const dataLength = dataBytes.byteLength;
        const dataChunkHash = "sha-256=" + hexToBase64(fullHash);

        const isUploaded = await authenticatedFetchRetry(user, `${coughMarshalRootUrl}/api/v1/uploads/${guid}`, {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/octet-stream',
                'Content-Range': `bytes 0-${dataLength - 1}/${dataLength}`,
                'Content-Disposition': `attachment; filename="${filename}"`,
                'Digest': dataChunkHash
            },
            body: dataBytes
        }, retryCount, retryInterval)
            .then(response => response.status === 200 || response.status === 202)
            .catch(() => false);
        return isUploaded;
    }

    static async uploadChunks(
        coughMarshalClient: CoughMarshalClient,
        user: AuthUser,
        sessionId: string,
        dispatch: React.Dispatch<DevicesAction>,
        deviceId: string,
        fileIdentifier: string,
        recordingType: RecordingFile,
        fileSize: number,
        chunkSize: number,
        recordingSource: (
            id: string,
            bytesFrom: number,
            bytesTo: number,
            recordingId: string) => Promise<Response>,
        fullHash: string,
        recordingId: string,
        retryCount?: number,
        retryInterval?: number
    ) {
        const totalChunks = Math.ceil(fileSize / chunkSize);

        const headResult = await authenticatedFetchRetry(user, `${coughMarshalRootUrl}/api/v1/uploads/${sessionId}`, {
            method: 'HEAD',
            headers: {
                'Content-Disposition': `attachment; filename="${fileIdentifier}"`
            }
        }, retryCount, retryInterval)
            .then(response => response.headers.get('Content-Length'));

        let startIndex = 0;
        if (!headResult) {
            return false;
        }

        if (parseInt(headResult) === fileSize) {
            return true;
        }
        else {
            startIndex = Math.floor(parseInt(headResult, 10) / chunkSize);
        }

        for (let chunkCount = startIndex; chunkCount < totalChunks; chunkCount++) {
            dispatch(SetUploadStatusAction.create(GenericMessages.UploadingStatusMessage(recordingType)));

            const rangeStart = chunkCount * chunkSize;
            let rangeEnd = rangeStart + chunkSize;
            rangeEnd = rangeEnd > fileSize ? fileSize : rangeEnd;
            rangeEnd--;

            try {
                const recordingResponse = await recordingSource(deviceId, rangeStart, rangeEnd, recordingId);

                const chunkHash = "sha-256=" + hexToBase64(fullHash);
                const buffer = await recordingResponse.arrayBuffer();

                const result = await authenticatedFetchRetry(user, `${coughMarshalRootUrl}/api/v1/uploads/${sessionId}`, {
                    method: 'PATCH',
                    headers: {
                        'Content-Type': 'application/octet-stream',
                        'Content-Range': `bytes ${rangeStart}-${rangeEnd}/${fileSize}`,
                        'Content-Encoding': 'gzip',
                        'Content-Disposition': `attachment; filename="${fileIdentifier}"`,
                        'Digest': chunkHash
                    },
                    body: gzip(buffer)
                }, retryCount, retryInterval)
                    .then(response => response.status === 200 || response.status === 202);

                if (!result)
                    return false;
            } catch {
                dispatch(SetUploadStatusAction.create(GenericMessages.FailedToUploadMessage));
                return false;
            }
        }
        return true;
    };
}
